import {
  Checkbox,
  FormControl,
  FormLabel,
  GridItem,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { SwitchType } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { HOUR_REGEX } from '~utils/timeBlocks';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayAutoRestart_ConfigFragment,
  useConfigUpdateAutoRestartMutation,
} from './__generated__/ConfigDisplayAutoRestart.graphql';

interface Props {
  config: ConfigDisplayAutoRestart_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

const getInitialState = (config: ConfigDisplayAutoRestart_ConfigFragment) => {
  const initialEnabled = config.autoRestart?.reported?.switch === 'ON' ? true : false;
  const initialTime = config.autoRestart?.reported?.time ?? '--:--';
  return { initialEnabled, initialTime };
};

export function ConfigDisplayAutoRestart({ config, configStatus }: Props) {
  const { verifyUserPermissions } = useAuth();
  const isConfigUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);
  const { initialEnabled, initialTime } = getInitialState(config);
  const [startTime, setStartTime] = useState<string>(initialTime);
  const [isAutoRestartEnabled, setIsAutoRestartEnabled] = useState<boolean>(initialEnabled);

  const updateTimeValue = (property: 'start' | 'end', value: string) => {
    if (!HOUR_REGEX.test(value)) return;
    setStartTime(value);
    handleUpdateAutoRestart(value, true);
  };
  const { t } = useTranslation();
  useEffect(() => {
    setIsAutoRestartEnabled(config.autoRestart?.reported?.switch === 'ON' ? true : false);
    setStartTime(config.autoRestart?.reported?.time ?? '--:--');
  }, [config?.autoRestart?.reported]);

  const toast = useToast();
  const [updateAutoRestart, { loading }] = useConfigUpdateAutoRestartMutation();

  const handleUpdateAutoRestart = useCallback(
    async (time: string, checkbox: boolean) => {
      try {
        if (time.length === 5) {
          await updateAutoRestart({
            variables: {
              input: {
                configurationId: config.id,
                switch: checkbox ? SwitchType.On : SwitchType.Off,
                time: time,
              },
            },
            refetchQueries: ['CustomerConfigDetailPage'],
            awaitRefetchQueries: true,
          });
        }
      } catch (err) {
        toast({
          status: 'error',
          title: t('autoRestartErr'),
          description: fromError(err, 'UpdateAutoRestart'),
        });
      }
    },
    [config, toast, updateAutoRestart, t],
  );

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAutoRestartEnabled(e.target.checked);
    handleUpdateAutoRestart(startTime, e.target.checked);
  };
  let disable = false;
  if (isConfigUpdateDisabled) {
    disable = true;
  } else if (!isAutoRestartEnabled) {
    disable = false;
  }
  return (
    <GridItem colSpan={{ base: 8, md: 7 }}>
      <Stack direction="column" spacing="10">
        {!isNil(config.autoRestart?.reported) && (
          <FormControl>
            <FormLabel>
              {t('autoRestart')}
              <FormLabelPendingIndicator isPending={loading} />
            </FormLabel>

            <Text color="gray.500" mb="2">
              {t('autoRestartText')}
            </Text>

            <Checkbox
              isChecked={isAutoRestartEnabled}
              onChange={handleCheckboxChange}
              isDisabled={isConfigUpdateDisabled}
            >
              {t('EnableAutoRestart')}
            </Checkbox>
            <FormControl maxWidth="80px" marginTop={5}>
              <TimeSelect
                value={isAutoRestartEnabled ? startTime : '--:--'}
                onChange={updateTimeValue}
                property="start"
                disabled={disable}
              />
            </FormControl>
          </FormControl>
        )}
      </Stack>
    </GridItem>
  );
}

ConfigDisplayAutoRestart.graphql = {
  fragments: {
    ConfigDisplayAutoRestart_config: gql`
      fragment ConfigDisplayAutoRestart_config on ConfigurationModel {
        id
        autoRestart {
          reported {
            switch
            time
          }
        }
      }
    `,
  },
  mutations: {
    ConfigUpdatePower: gql`
      mutation ConfigUpdateAutoRestart($input: ConfigurationUpdateAutoRestartInput!) {
        configurationUpdateAutoRestart(input: $input) {
          id
          ...ConfigDisplayAutoRestart_config
        }
      }
    `,
  },
};
